import { createGuideSearch, type GuideSearch } from "./search";
import type { Guide, SearchResult } from "./types";
import { loadGuides, type GuideFiles } from "./loader";

// Re-export types and functions for convenience
export type { Guide, SearchResult, SearchMatch, GuideFrontmatter } from "./types";
export { parseGuide, parseFrontmatter, renderMarkdown } from "./parser";
export { createGuideSearch, type GuideSearch } from "./search";
export { loadGuides, extractSlug, createGuideSearchFromFiles, type GuideFiles } from "./loader";

// Lazy-initialized guide search instance
let guideSearch: GuideSearch | null = null;

/**
 * Initialises the guide search with the provided guide files.
 * Call this once at application startup with the glob-imported files.
 *
 * @param guideFiles - Record of file paths to raw markdown content
 */
export function initialiseGuides(guideFiles: GuideFiles): void {
  const guides = loadGuides(guideFiles);
  guideSearch = createGuideSearch(guides);
}

/**
 * Gets the guide search instance, throwing if not initialised.
 */
function getGuideSearch(): GuideSearch {
  if (!guideSearch) {
    throw new Error("Guides not initialised. Call initialiseGuides() first.");
  }
  return guideSearch;
}

/**
 * Searches guides by query string.
 * Returns results ranked by relevance with match information.
 *
 * @param query - Search query string
 * @returns Array of search results with guide, score, and match details
 */
export function searchGuides(query: string): SearchResult[] {
  return getGuideSearch().search(query);
}

/**
 * Gets a single guide by its slug.
 *
 * @param slug - The guide's unique identifier (derived from filename)
 * @returns The guide if found, undefined otherwise
 */
export function getGuide(slug: string): Guide | undefined {
  return getGuideSearch().getGuide(slug);
}

/**
 * Gets all guides sorted by their order field.
 * Useful for building a table of contents.
 *
 * @returns Array of all guides sorted by order
 */
export function getAllGuides(): Guide[] {
  return getGuideSearch().getAllGuides();
}

/**
 * Resets the guide search instance.
 * Primarily used for testing.
 */
export function resetGuides(): void {
  guideSearch = null;
}
